<template>
  <HrPageLayout
    title="Appraisal Cycles"
    subtitle="Performance"
    :breadcrumb-items="[
      { label: 'Performance' },
      { label: 'Appraisal Cycles' }
    ]"
  >
    <div class="cycles-content">
      <!-- Filters -->
      <div class="hr-chart-card hr-mb-3">
        <div class="hr-chart-body">
          <div class="hr-form-grid hr-align-end" style="grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 1rem;">
            <div>
              <label class="hr-form-label">Year</label>
              <select v-model="filters.year" @change="loadCycles" class="hr-select">
                <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>
            <div>
              <label class="hr-form-label">Status</label>
              <select v-model="filters.status" @change="loadCycles" class="hr-select">
                <option value="">All</option>
                <option value="Draft">Draft</option>
                <option value="Active">Active</option>
                <option value="Closed">Closed</option>
              </select>
            </div>
            <div>
              <button class="hr-btn hr-btn-primary hr-w-100" @click="openCreateModal">
                <i class="bx bx-plus"></i> New Cycle
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Cycles Table -->
      <div class="hr-chart-card">
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p>Loading cycles...</p>
          </div>
          <div v-else-if="!cycles.length" class="hr-empty-state">
            <i class="hr-empty-icon bx bx-calendar"></i>
            <p>No appraisal cycles found. Create one to get started.</p>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-bordered hr-table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Self Due</th>
                  <th>Supervisor Due</th>
                  <th>HR Due</th>
                  <th>Status</th>
                  <th width="140">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in cycles" :key="c.id">
                  <td>{{ c.name }}</td>
                  <td>{{ formatDate(c.start_date) }}</td>
                  <td>{{ formatDate(c.end_date) }}</td>
                  <td>{{ c.self_assessment_deadline ? formatDate(c.self_assessment_deadline) : '-' }}</td>
                  <td>{{ c.supervisor_review_deadline ? formatDate(c.supervisor_review_deadline) : '-' }}</td>
                  <td>{{ c.hr_approval_deadline ? formatDate(c.hr_approval_deadline) : '-' }}</td>
                  <td>
                    <span :class="['hr-badge', getStatusClass(c.status)]">{{ c.status }}</span>
                  </td>
                  <td>
                    <button v-if="c.status === 'Draft'" class="hr-btn hr-btn-sm hr-btn-success hr-mr-1" @click="openLaunchModal(c)" title="Launch cycle">
                      <i class="bx bx-rocket"></i>
                    </button>
                    <button class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="viewCycle(c)" title="View">
                      <i class="bx bx-show"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Create Cycle Modal (Vue) -->
      <div v-if="showCreateModal" class="hr-modal-overlay" @click.self="closeCreateModal">
        <div class="hr-modal-dialog hr-modal-lg">
          <div class="hr-modal-content">
            <div class="hr-modal-header">
              <h5 class="hr-modal-title">Create Appraisal Cycle</h5>
              <button type="button" class="hr-modal-close" @click="closeCreateModal" aria-label="Close">
                <i class="bx bx-x"></i>
              </button>
            </div>
            <div class="hr-modal-body">
              <div class="hr-form-grid" style="grid-template-columns: 1fr; gap: 1rem;">
                <div>
                  <label class="hr-form-label">Cycle Name <span class="hr-text-danger">*</span></label>
                  <input v-model="form.name" type="text" class="hr-input" placeholder="e.g. Q1 2025 Performance Review">
                </div>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
                  <div>
                    <label class="hr-form-label">Start Date <span class="hr-text-danger">*</span></label>
                    <input v-model="form.start_date" type="date" class="hr-input">
                  </div>
                  <div>
                    <label class="hr-form-label">End Date <span class="hr-text-danger">*</span></label>
                    <input v-model="form.end_date" type="date" class="hr-input">
                  </div>
                </div>
                <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem;">
                  <div>
                    <label class="hr-form-label">Self-Assessment Due</label>
                    <input v-model="form.self_assessment_deadline" type="date" class="hr-input">
                  </div>
                  <div>
                    <label class="hr-form-label">Supervisor Review Due</label>
                    <input v-model="form.supervisor_review_deadline" type="date" class="hr-input">
                  </div>
                  <div>
                    <label class="hr-form-label">HR Approval Due</label>
                    <input v-model="form.hr_approval_deadline" type="date" class="hr-input">
                  </div>
                </div>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
                  <div>
                    <label class="hr-form-label">Self Weight (e.g. 0.30)</label>
                    <input v-model.number="form.self_weight" type="number" step="0.01" min="0" max="1" class="hr-input">
                  </div>
                  <div>
                    <label class="hr-form-label">Supervisor Weight (e.g. 0.70)</label>
                    <input v-model.number="form.supervisor_weight" type="number" step="0.01" min="0" max="1" class="hr-input">
                  </div>
                </div>
                <div>
                  <label class="hr-form-label">Department IDs (optional, comma-separated; leave blank for all)</label>
                  <input v-model="form.department_ids" type="text" class="hr-input" placeholder="e.g. 1,2,3">
                </div>
              </div>
            </div>
            <div class="hr-modal-footer">
              <button type="button" class="hr-btn hr-btn-secondary" @click="closeCreateModal">Cancel</button>
              <button type="button" class="hr-btn hr-btn-primary" @click="createCycle" :disabled="saving">
                {{ saving ? 'Creating...' : 'Create Cycle' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Launch Cycle Modal (Vue) -->
      <div v-if="launchCycle" class="hr-modal-overlay" @click.self="closeLaunchModal">
        <div class="hr-modal-dialog hr-modal-lg">
          <div class="hr-modal-content">
            <div class="hr-modal-header">
              <h5 class="hr-modal-title">Launch Cycle: {{ launchCycle.name || 'Untitled' }}</h5>
              <button type="button" class="hr-modal-close" @click="closeLaunchModal" aria-label="Close">
                <i class="bx bx-x"></i>
              </button>
            </div>
            <div class="hr-modal-body">
              <p class="hr-text-muted">This will create one appraisal per eligible employee for the period {{ formatDate(launchCycle.start_date) }} – {{ formatDate(launchCycle.end_date) }}. Employees who already have an appraisal in this period will be skipped.</p>
              <div v-if="launchLoading" class="hr-loading-wrap">
                <div class="hr-spinner"></div>
                <p>Loading eligible employees...</p>
              </div>
              <div v-else-if="eligibleEmployees.length" class="hr-table-wrapper" style="max-height: 300px;">
                <table class="hr-table hr-table-sm hr-table-bordered">
                  <thead>
                    <tr>
                      <th><input type="checkbox" v-model="selectAllEligible" @change="toggleAllEligible"></th>
                      <th>Employee</th>
                      <th>Position</th>
                      <th>Department</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="emp in eligibleEmployees" :key="emp.id">
                      <td><input type="checkbox" :value="emp.id" v-model="selectedEmployeeIds"></td>
                      <td>{{ emp.fullName }}</td>
                      <td>{{ emp.positionTitle }}</td>
                      <td>{{ emp.department }}</td>
                    </tr>
                  </tbody>
                </table>
                <p class="hr-form-hint hr-mt-2">{{ selectedEmployeeIds.length }} selected</p>
              </div>
              <div v-else class="hr-text-muted">No eligible employees (or all already have appraisals for this period).</div>
            </div>
            <div class="hr-modal-footer">
              <button type="button" class="hr-btn hr-btn-secondary" @click="closeLaunchModal">Cancel</button>
              <button type="button" class="hr-btn hr-btn-success" @click="doLaunchCycle" :disabled="launching || !selectedEmployeeIds.length">
                {{ launching ? 'Launching...' : 'Launch Cycle' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";

export default {
  name: "AppraisalCyclesManagement",
  components: { HrPageLayout },
  data() {
    const y = new Date().getFullYear();
    return {
      loading: false,
      saving: false,
      launching: false,
      launchLoading: false,
      showCreateModal: false,
      cycles: [],
      filters: { year: String(y), status: '' },
      years: [y, y - 1, y - 2],
      form: {
        name: '',
        start_date: '',
        end_date: '',
        self_assessment_deadline: '',
        supervisor_review_deadline: '',
        hr_approval_deadline: '',
        self_weight: 0.30,
        supervisor_weight: 0.70,
        department_ids: ''
      },
      launchCycle: null,
      eligibleEmployees: [],
      selectedEmployeeIds: [],
      selectAllEligible: false
    };
  },
  mounted() {
    this.loadCycles();
  },
  methods: {
    async loadCycles() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getAppraisalCycles");
        data.append("year", this.filters.year);
        if (this.filters.status) data.append("status", this.filters.status);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success && Array.isArray(res.data.data)) {
          this.cycles = res.data.data;
        } else {
          this.cycles = [];
        }
      } catch (e) {
        this.cycles = [];
      } finally {
        this.loading = false;
      }
    },
    openCreateModal() {
      this.form = {
        name: '',
        start_date: '',
        end_date: '',
        self_assessment_deadline: '',
        supervisor_review_deadline: '',
        hr_approval_deadline: '',
        self_weight: 0.30,
        supervisor_weight: 0.70,
        department_ids: ''
      };
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
    },
    async createCycle() {
      if (!this.form.name || !this.form.start_date || !this.form.end_date) {
        this.$toast?.error?.('Name, start date and end date are required') || alert('Name, start date and end date are required');
        return;
      }
      this.saving = true;
      try {
        const data = new FormData();
        data.append("function", "createAppraisalCycle");
        data.append("name", this.form.name);
        data.append("start_date", this.form.start_date);
        data.append("end_date", this.form.end_date);
        if (this.form.self_assessment_deadline) data.append("self_assessment_deadline", this.form.self_assessment_deadline);
        if (this.form.supervisor_review_deadline) data.append("supervisor_review_deadline", this.form.supervisor_review_deadline);
        if (this.form.hr_approval_deadline) data.append("hr_approval_deadline", this.form.hr_approval_deadline);
        data.append("self_weight", this.form.self_weight);
        data.append("supervisor_weight", this.form.supervisor_weight);
        if (this.form.department_ids) data.append("department_ids", this.form.department_ids);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success?.('Cycle created') || alert('Cycle created');
          this.closeCreateModal();
          this.loadCycles();
        } else {
          this.$toast?.error?.(res?.data?.message || 'Failed to create') || alert(res?.data?.message || 'Failed to create');
        }
      } catch (e) {
        this.$toast?.error?.('Failed to create cycle') || alert('Failed to create cycle');
      } finally {
        this.saving = false;
      }
    },
    async openLaunchModal(cycle) {
      this.launchCycle = cycle;
      this.selectedEmployeeIds = [];
      this.selectAllEligible = false;
      this.launchLoading = true;
      try {
        const data = new FormData();
        data.append("function", "getEligibleEmployeesForCycle");
        data.append("cycle_id", cycle.id);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success && Array.isArray(res.data.data)) {
          this.eligibleEmployees = res.data.data;
          this.selectedEmployeeIds = res.data.data.map(e => e.id);
          this.selectAllEligible = true;
        } else {
          this.eligibleEmployees = [];
        }
      } catch (e) {
        this.eligibleEmployees = [];
      } finally {
        this.launchLoading = false;
      }
    },
    closeLaunchModal() {
      this.launchCycle = null;
    },
    toggleAllEligible() {
      if (this.selectAllEligible) {
        this.selectedEmployeeIds = this.eligibleEmployees.map(e => e.id);
      } else {
        this.selectedEmployeeIds = [];
      }
    },
    async doLaunchCycle() {
      if (!this.launchCycle || !this.selectedEmployeeIds.length) return;
      this.launching = true;
      try {
        const data = new FormData();
        data.append("function", "launchAppraisalCycle");
        data.append("cycle_id", this.launchCycle.id);
        data.append("employee_ids", this.selectedEmployeeIds.join(','));
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          const created = res.data.data?.created ?? 0;
          this.$toast?.success?.(`Cycle launched. ${created} appraisal(s) created.`) || alert(`Cycle launched. ${created} appraisal(s) created.`);
          this.closeLaunchModal();
          this.loadCycles();
        } else {
          this.$toast?.error?.(res?.data?.message || 'Launch failed') || alert(res?.data?.message || 'Launch failed');
        }
      } catch (e) {
        this.$toast?.error?.('Failed to launch cycle') || alert('Failed to launch cycle');
      } finally {
        this.launching = false;
      }
    },
    viewCycle(c) {
      this.$router.push({ path: '/hr-dashboard', query: { cycle_id: c.id } });
    },
    formatDate(d) {
      if (!d) return '-';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    getStatusClass(s) {
      if (s === 'Draft') return 'hr-badge-secondary';
      if (s === 'Active') return 'hr-badge-primary';
      if (s === 'Closed') return 'hr-badge-success';
      return 'hr-badge-secondary';
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.hr-mb-3 { margin-bottom: 1rem; }
.hr-mr-1 { margin-right: 0.25rem; }
.hr-mt-2 { margin-top: 0.5rem; }
.hr-w-100 { width: 100%; }

.hr-modal-lg { max-width: 800px; }
</style>
